import { User, Building2, Globe, Tag, FileText, Check } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { KontaktDetail, KontaktTag } from '@/utils/api'

interface StammdatenTabProps {
  kontakt: KontaktDetail
  allTags: KontaktTag[]
  getValue: <K extends keyof KontaktDetail>(field: K) => KontaktDetail[K] | undefined
  onChange: (field: keyof KontaktDetail, value: any) => void
}

export function StammdatenTab({ kontakt, allTags, getValue, onChange }: StammdatenTabProps) {
  const isPerson = kontakt.kontakt_type === 'person'
  const currentTags = (getValue('tags') as KontaktTag[] | undefined) || kontakt.tags

  const toggleTag = (tag: KontaktTag) => {
    const isSelected = currentTags.some(t => t.id === tag.id)
    if (isSelected) {
      onChange('tags', currentTags.filter(t => t.id !== tag.id))
    } else {
      onChange('tags', [...currentTags, tag])
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column - 2/3 width */}
      <div className="lg:col-span-2 space-y-6">
        {/* Identity Section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            {isPerson ? (
              <User className="h-5 w-5 text-slate-400" />
            ) : (
              <Building2 className="h-5 w-5 text-slate-400" />
            )}
            {isPerson ? 'Persönliche Daten' : 'Unternehmensdaten'}
          </h2>

          {isPerson ? (
            // Person fields
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="anrede">Anrede</Label>
                <Select
                  value={getValue('anrede') || 'none'}
                  onValueChange={(value) => onChange('anrede', value === 'none' ? '' : value)}
                >
                  <SelectTrigger id="anrede">
                    <SelectValue placeholder="Anrede wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="herr">Herr</SelectItem>
                    <SelectItem value="frau">Frau</SelectItem>
                    <SelectItem value="none">Keine Anrede</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="date_of_birth">Geburtsdatum</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={getValue('date_of_birth') || ''}
                  onChange={(e) => onChange('date_of_birth', e.target.value || null)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="first_name">Vorname</Label>
                <Input
                  id="first_name"
                  type="text"
                  value={getValue('first_name') || ''}
                  onChange={(e) => onChange('first_name', e.target.value)}
                  placeholder="Max"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">Nachname</Label>
                <Input
                  id="last_name"
                  type="text"
                  value={getValue('last_name') || ''}
                  onChange={(e) => onChange('last_name', e.target.value)}
                  placeholder="Mustermann"
                />
              </div>
            </div>
          ) : (
            // Company fields
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="company_name">Firmenname</Label>
                <Input
                  id="company_name"
                  type="text"
                  value={getValue('company_name') || ''}
                  onChange={(e) => onChange('company_name', e.target.value)}
                  placeholder="Muster GmbH"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="legal_form">Rechtsform</Label>
                <Input
                  id="legal_form"
                  type="text"
                  value={getValue('legal_form') || ''}
                  onChange={(e) => onChange('legal_form', e.target.value)}
                  placeholder="GmbH, AG, etc."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="registration_number">Handelsregisternummer</Label>
                <Input
                  id="registration_number"
                  type="text"
                  value={getValue('registration_number') || ''}
                  onChange={(e) => onChange('registration_number', e.target.value)}
                  placeholder="HRB 12345"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="tax_id">USt-IdNr.</Label>
                <Input
                  id="tax_id"
                  type="text"
                  value={getValue('tax_id') || ''}
                  onChange={(e) => onChange('tax_id', e.target.value)}
                  placeholder="DE123456789"
                />
              </div>
            </div>
          )}
        </div>

        {/* Special Identifiers */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Globe className="h-5 w-5 text-slate-400" />
            Spezielle Kennungen
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="iata_code">IATA-Code</Label>
              <Input
                id="iata_code"
                type="text"
                maxLength={3}
                value={getValue('iata_code') || ''}
                onChange={(e) => onChange('iata_code', e.target.value.toUpperCase())}
                placeholder="LH"
                className="font-mono uppercase"
              />
              <p className="text-xs text-slate-500">
                2-3 Zeichen für Airlines (z.B. LH, BA, AF)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="portal_url">Portal-URL</Label>
              <Input
                id="portal_url"
                type="url"
                value={getValue('portal_url') || ''}
                onChange={(e) => onChange('portal_url', e.target.value)}
                placeholder="https://..."
              />
              <p className="text-xs text-slate-500">
                RSV-Portal, Gerichtswebsite, etc.
              </p>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-slate-400" />
            Notizen
          </h2>

          <Textarea
            value={getValue('notes') || ''}
            onChange={(e) => onChange('notes', e.target.value)}
            placeholder="Interne Notizen zu diesem Kontakt..."
            rows={4}
          />
        </div>
      </div>

      {/* Right Column - 1/3 width */}
      <div className="space-y-6">
        {/* Tags */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Tag className="h-5 w-5 text-slate-400" />
            Tags
          </h2>

          <div className="space-y-2">
            {allTags.map((tag) => {
              const isSelected = currentTags.some(t => t.id === tag.id)
              return (
                <button
                  key={tag.id}
                  onClick={() => toggleTag(tag)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-colors ${
                    isSelected
                      ? 'bg-slate-900 border-slate-900 text-white'
                      : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <span className="font-medium">{tag.display_name}</span>
                  {isSelected && <Check className="h-4 w-4" />}
                </button>
              )
            })}
          </div>

          {allTags.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-4">
              Keine Tags verfügbar
            </p>
          )}
        </div>

        {/* Status */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">
            Status
          </h2>

          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
            <div>
              <p className="font-medium text-slate-900">Aktiv</p>
              <p className="text-sm text-slate-500">
                Kontakt ist aktiv und wird in Listen angezeigt
              </p>
            </div>
            <Switch
              checked={getValue('is_active') ?? true}
              onCheckedChange={(checked) => onChange('is_active', checked)}
            />
          </div>

          {kontakt.is_shared && (
            <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-100">
              <p className="text-sm text-blue-700">
                <strong>Geteilter Kontakt:</strong> Dieser Kontakt ist für alle Organisationen sichtbar.
              </p>
            </div>
          )}
        </div>

        {/* Metadata */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">
            Metadaten
          </h2>

          <dl className="space-y-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-500">UUID</dt>
              <dd className="text-slate-900 font-mono text-xs">
                {kontakt.uuid.slice(0, 8)}...
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Erstellt</dt>
              <dd className="text-slate-900">
                {new Date(kontakt.created_at).toLocaleDateString('de-DE')}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Aktualisiert</dt>
              <dd className="text-slate-900">
                {new Date(kontakt.updated_at).toLocaleDateString('de-DE')}
              </dd>
            </div>
            {kontakt.user_email && (
              <div className="flex justify-between">
                <dt className="text-slate-500">Portal-Benutzer</dt>
                <dd className="text-slate-900 truncate max-w-[150px]">
                  {kontakt.user_email}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </div>
  )
}
